import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AsyncSurface from "../AsyncSurface";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * InstructorStats — Phase 11.2 (Feb 6, 2026): split for the Progress
 * vs. Money tabs.
 *
 * Per the locked Phase 11.2 map (Feb 6, 2026): Lessons taught / hours
 * are PROGRESS data; revenue figures are MONEY data. They must NOT
 * appear on the same card. This component now has 2 variants:
 *
 *   variant="performance" → lessons today · lessons this week ·
 *                            unique pupils (defaults — Progress tab)
 *   variant="revenue"     → revenue today · revenue this week ·
 *                            paid lessons count (Money tab)
 *
 * If `stats` is passed via prop the component renders synchronously;
 * otherwise it self-fetches from /api/bookings/stats so the new
 * lean tab pages can mount it standalone.
 *
 * Phase 1.3 alive-contract migration (May 19, 2026):
 *   • Self-fetch path now uses <AsyncSurface> so the surface always
 *     answers loading / failed / empty truthfully. The previous
 *     fetch-error fallback `setStats(emptyObject)` silently rendered
 *     £0/£0/0 pretending everything was fine — that was a doctrine
 *     breach.
 *   • Prop-injected path is unchanged (caller controls rendering).
 *   • Visual layout when content is present is unchanged.
 */
export function InstructorStats({ stats: statsProp = null, variant = "performance", testidPrefix = "stat" }) {
  const usingProp = statsProp != null;
  const [stats, setStats] = useState(statsProp);
  const [loading, setLoading] = useState(!usingProp);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/bookings/stats`, { withCredentials: true });
      setStats(r.data || {});
    } catch (e) {
      setStats(null);
      setError(e?.message || "Couldn't load your stats just now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (usingProp) {
      setStats(statsProp);
      setLoading(false);
      setError(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/bookings/stats`, { withCredentials: true });
        if (alive) {
          setStats(r.data || {});
          setLoading(false);
        }
      } catch (e) {
        if (alive) {
          setStats(null);
          setError(e?.message || "Couldn't load your stats just now.");
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [statsProp, usingProp]);

  const items = variant === "revenue"
    ? [
        { key: "revenue_today",     label: "Today",      color: "text-emerald-600 dark:text-emerald-400", money: true },
        { key: "revenue_this_week", label: "This Week",  color: "text-emerald-600 dark:text-emerald-400", money: true },
        { key: "paid_lessons",      label: "Paid lessons", color: "text-blue-600 dark:text-blue-400" },
      ]
    : [
        { key: "today",         label: "Lessons today",     color: "text-blue-600 dark:text-blue-400" },
        { key: "this_week",     label: "Lessons this week", color: "text-emerald-600 dark:text-emerald-400" },
        { key: "unique_pupils", label: "Pupils",            color: "text-violet-600 dark:text-violet-400" },
      ];

  const fmt = (v, money) => money
    ? `£${(((v ?? 0) / 100) || 0).toFixed(0)}`
    : (v ?? 0);

  // Empty heuristic — a brand-new instructor whose stats endpoint
  // returns an empty object should see a calm "No stats yet" rather
  // than three confident "£0 / £0 / 0" tiles that read as broken.
  // Only applies to the self-fetched path; prop-injected callers
  // decide their own empty contract.
  const isEmpty = !usingProp && stats !== null && items.every(({ key }) => {
    const v = stats?.[key];
    return v === undefined || v === null || v === 0;
  });

  const emptyLabel = variant === "revenue"
    ? "No income yet this week."
    : "No lessons logged yet.";
  const loadingLabel = variant === "revenue"
    ? "Loading your income summary…"
    : "Loading your stats…";

  const grid = (
    <div data-testid={`${testidPrefix}-row`} className="grid grid-cols-3 gap-3">
      {items.map(({ key, label, color, money }) => (
        <div
          key={key}
          data-testid={`${testidPrefix}-${key}`}
          className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 px-3 py-3 text-center"
        >
          <p className={`text-2xl font-extrabold ${color}`}>{fmt(stats?.[key], money)}</p>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">{label}</p>
        </div>
      ))}
    </div>
  );

  // Prop-injected callers skip the alive-contract wrapper because
  // they own the lifecycle. Self-fetch path gets the full contract.
  if (usingProp) return grid;

  return (
    <AsyncSurface
      testid={testidPrefix}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyLabel={emptyLabel}
      loadingLabel={loadingLabel}
      onRetry={fetchStats}
    >
      {grid}
    </AsyncSurface>
  );
}
